import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatRate, formatTrend, trend as computeTrend, type Trend } from '@/lib/metrics';

/**
 * VFStatCard — the shared metric tile (W1 brick, DESIGN.md §4, DES-12/19).
 *
 * Give it `metric` and it applies the figure rules itself:
 *   - loading → skeleton; failed (`state="error"`) → « Indisponible », never 0;
 *   - a rate with no denominator (null) → « — »;
 *   - no previous period → no trend and no sparkline;
 *   - a rate's trend in points, a count's in relative %;
 *   - `hint` (period and denominator) as a tooltip.
 * The older `value` / `delta` / `deltaLabel` props still work.
 * It is a button only when it does something (`onClick`).
 */
interface VFStatCardProps {
  label: ReactNode;
  value?: ReactNode;
  delta?: number;
  deltaLabel?: string;
  icon?: ReactNode;
  className?: string;
  onClick?: () => void;
  state?: 'ready' | 'loading' | 'error';
  metric?: { current: number | null; previous?: number | null; kind: 'count' | 'rate' };
  /** Points of the period, oldest first; drawn only with a previous period. */
  series?: number[];
  /** Which way is good, for the trend colour (default: up). */
  goodWhen?: 'up' | 'down';
  /** Period and denominator, shown on hover and read by screen readers. */
  hint?: string;
  /**
   * QA-W6-2: `text` for a value in words (« Transcription + Jev »): smaller,
   * proportional, wraps inside the tile, full text in the tooltip. A figure
   * keeps the large monospace digits.
   */
  valueKind?: 'figure' | 'text';
}

function Sparkline({ points }: { points: number[] }) {
  const w = 72, h = 22;
  const max = Math.max(...points), min = Math.min(...points);
  const span = max - min || 1;
  const d = points.map((p, i) => `${(i / (points.length - 1)) * w},${h - 2 - ((p - min) / span) * (h - 4)}`).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden data-testid="stat-sparkline" className="shrink-0">
      <polyline points={d} fill="none" stroke="var(--vf-accent)" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export function VFStatCard({
  label, value, delta, deltaLabel, icon, className = '', onClick,
  state = 'ready', metric, series, goodWhen = 'up', hint, valueKind = 'figure',
}: VFStatCardProps) {
  const { t, i18n } = useTranslation(['common']);
  const locale = i18n.language;

  let shown: ReactNode = value;
  let tr: Trend | null = null;
  if (metric) {
    shown = metric.kind === 'rate'
      ? formatRate(metric.current, locale)
      : metric.current === null ? '—' : new Intl.NumberFormat(locale).format(metric.current);
    tr = computeTrend(metric.current, metric.previous ?? null, metric.kind);
  }
  if (state === 'error') shown = t('common:stat.unavailable');

  const legacy = typeof delta === 'number' ? delta : null;
  const deltaText = metric ? formatTrend(tr, locale) : (deltaLabel ?? (legacy !== null ? `${legacy > 0 ? '+' : ''}${legacy}%` : null));
  const dir = metric ? tr?.direction : legacy !== null ? (legacy > 0 ? 'up' : legacy < 0 ? 'down' : 'flat') : (deltaLabel || '').startsWith('+') ? 'up' : (deltaLabel || '').startsWith('-') ? 'down' : 'flat';
  const good = dir === 'flat' || !dir ? null : (dir === goodWhen);
  const deltaColor = good === null ? 'var(--vf-text-3)' : good ? 'var(--vf-status-connected-fg)' : 'var(--vf-status-failed-fg)';
  const showTrend = state === 'ready' && !!deltaText;
  const showSpark = state === 'ready' && !!series && series.length >= 2 && (!metric || tr !== null);

  const body = (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-medium uppercase tracking-wider text-[var(--vf-text-3)]">{label}</div>
        {state === 'loading' ? (
          <div className="mt-2 h-7 w-20 animate-pulse rounded bg-[var(--vf-surface-3)] motion-reduce:animate-none" aria-label={t('common:actions.loading')} role="status" />
        ) : (
          <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span
              className={state === 'error' ? 'text-[14px] font-medium text-[var(--vf-text-2)]'
                : valueKind === 'text' ? 'min-w-0 max-w-full break-words line-clamp-2 text-[18px] font-semibold leading-tight text-[var(--vf-text-1)]'
                : 'font-vf-mono text-[28px] font-semibold leading-none text-[var(--vf-text-1)] tabular-nums'}
              title={state !== 'error' && valueKind === 'text' && typeof shown === 'string' ? shown : undefined}
              data-value-kind={valueKind}
            >
              {shown}
            </span>
            {showTrend && <span className="text-xs font-medium" style={{ color: deltaColor }}>{deltaText}</span>}
          </div>
        )}
        {hint && <span className="sr-only">{hint}</span>}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-2">
        {icon && (
          <span className="flex h-8 w-8 items-center justify-center rounded-vf-sm bg-[var(--vf-surface-3)] text-[var(--vf-text-2)]">{icon}</span>
        )}
        {showSpark && <Sparkline points={series!} />}
      </div>
    </div>
  );

  const cls = [
    'group w-full rounded-vf-lg border border-[var(--vf-border)] bg-[var(--vf-surface-2)] p-4 text-left',
    onClick ? 'cursor-pointer transition-colors hover:bg-[var(--vf-surface-3)]' : '',
    className,
  ].join(' ');
  return onClick
    ? <button type="button" onClick={onClick} title={hint} className={cls}>{body}</button>
    : <div title={hint} className={cls} data-testid="stat-card">{body}</div>;
}

export default VFStatCard;
